/**
 * Posting, editing and deleting in a channel.
 *
 * One hook rather than three exported mutations, because a caller that gets the
 * refresh wrong is the failure mode this replaces: the legacy `useCreateMessage`
 * invalidated five keys by hand, and the visit-scenario writes (accept, decline, share
 * availability) each forgot a different one.
 *
 * Every write refreshes what its route announces (`useConversationRefresh`). A message
 * carrying a visit scenario also rewrites the visit its card reads — the visit row, its
 * time proposal and preference, the order's visit list — so a scenario that names its
 * visit refreshes `visit.changed` too. One that creates the visit (a fresh availability
 * request or proposal) cannot name it: it refreshes the rows the cards read
 * (`visitCardKeys`), and the platform announces the visit itself, with no
 * origin, so the screen's realtime subscription (`ChannelView` listens on the
 * conversation's order) refreshes the lists for this tab as for every other.
 *
 * A post carrying files into an order's thread or notes, or an edit that sends a
 * message's files, also refreshes the order's gallery and its home's Files tab
 * (`orderAttachmentKeys`) — the files are attached to the order inside the same request.
 */

import { useGetAuthMe } from "@birdwatch/client";
import type { QueryKey } from "@tanstack/react-query";
import { useCallback } from "react";

import {
  orderAttachmentsRefreshKeys,
  refreshKeysFor,
} from "@/features/shared/realtime-refresh";
import {
  addPendingSend,
  markPendingSent,
  removePendingSend,
} from "../_hooks/use-pending-sends";
import { orderIdOfCid } from "../_libs/channel-cid";
import { buildOptimisticMessage } from "../_libs/optimistic-message";
import {
  type ChannelQuotedMessage,
  type CreateChannelMessageInput,
  type UpdateChannelMessageInput,
  useCreateChannelsMessagesByCid,
  useDeleteChannelsMessagesByCidByMessageId,
  useUpdateChannelsMessagesByCidByMessageId,
} from "./channel-endpoints";
import { useConversationRefresh } from "./use-channel-invalidate";
import { useChannelMessageCache } from "./use-channel-message-cache";
import { visitCardKeys } from "./use-visit-scenario-invalidate";

/**
 * Does this payload do more than post text?
 *
 * Pure — the fields are exactly the visit scenarios `POST /channels/:cid/messages`
 * accepts beyond `comment`/`uploads`.
 */
function carriesVisitScenario(input: CreateChannelMessageInput): boolean {
  return Boolean(
    input.visitId ||
      input.isTimePreferenceRequest ||
      input.isTimeProposalRequest ||
      input.isAccept ||
      input.isDecline ||
      input.isReschedule ||
      input.isBookVisit ||
      input.isAddToCalendar ||
      (input.timePreferences && input.timePreferences.length > 0) ||
      (input.technicianDuration && input.technicianDuration.length > 0),
  );
}

/**
 * What else a post made stale: nothing for plain text; for a visit scenario, the
 * `visit.changed` set when the post names its visit, else the rows its card reads.
 */
function visitScenarioKeys(
  cid: string,
  input: CreateChannelMessageInput,
): QueryKey[] {
  if (!carriesVisitScenario(input)) return [];
  const orderId = orderIdOfCid(cid);
  return orderId && input.visitId
    ? refreshKeysFor("visit.changed", { orderId, visitId: input.visitId })
    : visitCardKeys();
}

/**
 * Does this post attach files? Its `uploads`, or the `visitUploads` a visit scenario
 * links to its visit — the same test the route uses to announce the order's files.
 *
 * Pure.
 */
function carriesFiles(input: CreateChannelMessageInput): boolean {
  return (
    (input.uploads?.length ?? 0) > 0 || (input.visitUploads?.length ?? 0) > 0
  );
}

/**
 * What a write that touched files made stale beyond the conversation: files sent into
 * an order's thread or its internal notes also land in that order's gallery and its
 * home's Files tab, which the message events do not refresh. Nothing when the write
 * touched no file, or outside an order (a home's support conversation keeps its files
 * on the message).
 *
 * Pure.
 */
function orderAttachmentKeys(cid: string, touchedFiles: boolean): QueryKey[] {
  const orderId = orderIdOfCid(cid);
  return orderId && touchedFiles ? orderAttachmentsRefreshKeys(orderId) : [];
}

/**
 * Strictly-decreasing placeholder counter.
 *
 * Module-level, not a per-hook ref: two mounted surfaces on one channel (a visit
 * dialog beside the composer) would otherwise both mint `optimistic:-1` and remove
 * each other's placeholder.
 */
let lastTempId = 0;
const nextTempId = () => --lastTempId;

export function useChannelMessageMutations(cid: string) {
  const refreshConversation = useConversationRefresh(cid);
  const patchMessage = useChannelMessageCache(cid);
  const { data: me } = useGetAuthMe().get();

  // A post is refreshed in `sendMessage`, which decides whether to wait for it.
  const createMutation = useCreateChannelsMessagesByCid();
  // The new text goes in first, so the edit row collapses over the edited message rather
  // than over the old one. The route answers ids only, so the text comes from the request
  // it just accepted; `updatedAt` is what renders the "(Edited)" mark. Attachments are NOT
  // patched — `uploads` carries ids, not the rows the bubble draws — and the refresh
  // settles them. Sending `uploads` replaces the message's files, and the body cannot tell
  // an added file from a removed one: the route announces the order's files for any edit
  // that sends the set, and so does this.
  //
  // The refresh is NOT returned. Returning it keeps `mutateAsync` pending until every
  // mounted query has refetched, which is a visible wait on whatever ordered the write.
  const updateMutation = useUpdateChannelsMessagesByCidByMessageId({
    onSuccess: (message, { body }) => {
      if (body?.body !== undefined) {
        const editedAt = new Date().toISOString();
        patchMessage(message.id, (cached) => ({
          ...cached,
          body: body.body ?? null,
          updatedAt: editedAt,
        }));
      }
      void refreshConversation(
        { channelId: message.channelId, messageId: message.id },
        orderAttachmentKeys(cid, body?.uploads != null),
      );
    },
  });
  // The row goes first, so the delete dialog closes over a thread that no longer holds the
  // message — the correctness awaiting the refetch used to buy, now in the same frame. A
  // deleted message can take a visit proposal with it — the server drops the preference and
  // proposal rows it carried — and the route names no visit, so the visit cards refresh too.
  const deleteMutation = useDeleteChannelsMessagesByCidByMessageId({
    onSuccess: (message) => {
      patchMessage(message.id, () => null);
      void refreshConversation(
        { channelId: message.channelId, messageId: message.id },
        visitCardKeys(),
      );
    },
  });

  const sendMessage = useCallback(
    async (
      input: CreateChannelMessageInput,
      options: {
        // What the placeholder draws as its quote card until the real row lands — the
        // input carries only `quotedMessageId`.
        quotedMessage?: ChannelQuotedMessage | null;
        // Request headers for this post only (the Smart Action a draft came from).
        headers?: Record<string, string>;
        /**
         * Resolve as soon as the post lands, without waiting for the thread refetch —
         * for a caller whose OWN ui is the feedback, like a dialog that closes on
         * success. Default false: a composer send keeps waiting, because without a
         * placeholder the thread shows nothing until the refetch lands.
         */
        settleOnSend?: boolean;
      } = {},
    ) => {
      // Optimism covers a plain root send only. A reply lives in a separate thread query
      // this cache does not hold, and a visit scenario renders a block the text-only
      // placeholder cannot — both fall back to the post-send scroll + refetch.
      const optimistic =
        input.replyToId == null && !carriesVisitScenario(input) && me?.id
          ? buildOptimisticMessage({
              tempId: nextTempId(),
              authorId: me.id,
              input,
              quotedMessage: options.quotedMessage ?? null,
              nowIso: new Date().toISOString(),
            })
          : null;

      if (optimistic) addPendingSend(cid, optimistic);

      try {
        const result = await createMutation.mutateAsync({
          params: { cid },
          body: input,
          headers: options.headers,
        });

        const refreshed = refreshConversation(
          { channelId: result.channelId, messageId: result.id },
          [
            ...visitScenarioKeys(cid, input),
            ...orderAttachmentKeys(cid, carriesFiles(input)),
          ],
        );

        if (optimistic) {
          // Hand the placeholder the id its real row will carry and stop here. It is
          // retired by the THREAD, when a fetched page actually contains that id
          // (`reapLandedPendingSends`) — never by this function awaiting a refetch.
          // Awaiting here is what broke a burst of sends: `invalidateQueries` cancels
          // an in-flight refetch by default, so the next send's refresh killed this
          // one's, the await resolved with the row still absent, and the placeholder
          // was dropped out from under a message still on its way. The refresh above
          // is already on its way.
          markPendingSent(cid, optimistic.id, result.id);
        } else if (!options.settleOnSend) {
          // Nothing stands in for this one — a thread reply or a visit scenario has no
          // placeholder — so the thread shows nothing until the refetch lands. Wait.
          await refreshed;
        }

        return result;
      } catch (error) {
        // Nothing to stand in for any more. The composer separately restores the text
        // and toasts.
        if (optimistic) removePendingSend(cid, optimistic.id);
        throw error;
      }
    },
    [createMutation, cid, refreshConversation, me?.id],
  );

  const editMessage = useCallback(
    (messageId: string, input: UpdateChannelMessageInput) =>
      updateMutation.mutateAsync({ params: { cid, messageId }, body: input }),
    [updateMutation, cid],
  );

  const deleteMessage = useCallback(
    (messageId: string) =>
      deleteMutation.mutateAsync({ params: { cid, messageId } }),
    [deleteMutation, cid],
  );

  return {
    sendMessage,
    editMessage,
    deleteMessage,
    isSending: createMutation.isPending,
    isEditing: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
}
